import React, { createContext, useContext, ReactNode } from 'react';
import { useMediaQuery } from '@mantine/hooks';

// Context Type
interface MediaQueryContextType {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

// Declare MediaQueryContext
export const MediaQueryContext = createContext<MediaQueryContextType | null>(null);

// Provider Component
export const MediaQueryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Standard breakpoints matching the app's usage
  // useMediaQuery is undefined until its first effect runs; treat that as false.
  const isMobile = useMediaQuery('(max-width: 768px)') ?? false;
  const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1024px)') ?? false;
  const isDesktop = useMediaQuery('(min-width: 1025px)') ?? false;

  return (
    <MediaQueryContext.Provider value={{ isMobile, isTablet, isDesktop }}>
      {children}
    </MediaQueryContext.Provider>
  );
};

// Custom Hook for Consuming Media Query Context
export const useMediaQueryContext = () => {
  const context = useContext(MediaQueryContext);

  if (!context) {
    throw new Error('❌ useMediaQueryContext must be used within a <MediaQueryProvider>');
  }

  return context;
};

// Convenience hooks for individual breakpoints
export const useIsMobile = () => {
  const { isMobile } = useMediaQueryContext();
  return isMobile;
};

export const useIsTablet = () => {
  const { isTablet } = useMediaQueryContext();
  return isTablet;
};

export const useIsDesktop = () => {
  const { isDesktop } = useMediaQueryContext();
  return isDesktop;
};
